
import Vue from 'vue';
import FormInput from '@core/components/UI/form/FormInput.vue';
import FormSelect from '@core/components/UI/form/FormSelect.vue';
import FormSelectMultiple from '@core/components/UI/form/FormSelectMultiple.vue';
import SearchDatePicker from '@root/modules/search/components/SearchDatePicker.vue';

import { TranslateResult } from 'vue-i18n';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/lt';
import { searchCategories as categories } from '@root/modules/search/utils/config';
import { DefaultSearchVariables } from '@root/modules/search/types/SearchVariables';
import { GetUniversalHeadlines } from '@root/modules/headlines/types/headlines';
import { normalizeSearchObj } from '@root/modules/search/utils/normalizeSearchObj';
import isMobileView from '@root/common/utils/isMobileView';
import { faArrowRotateLeft, faMagnifyingGlass } from '@delfi.lt/fa-kit/icons/modules/classic/solid';

dayjs.locale('lt');
dayjs.extend(timezone);
dayjs.tz.setDefault('Europe/Vilnius');

type DateRange = {
  from: string | null;
  to: string | null;
};

interface SelectOption {
  name: string | TranslateResult;
  value: string | null;
  categories?: number[];
}

interface Data {
  date: DateRange;
  localVariables: DefaultSearchVariables;
  searchFormData: {
    categories: SelectOption[] | null;
    order: SelectOption[] | null;
  };
  isMobileView: boolean;
}

interface Methods {
  handleReset: () => void;
  handleSetDate: (value: Date[]) => void;
  handleSetCategory: (value: string[]) => void;
  submit: () => void;
}

interface Props {
  variables: DefaultSearchVariables;
  showCategorySelect: boolean;
}

export default Vue.extend<Data, Methods, unknown, Props>({
  components: {
    FormInput,
    FormSelect,
    FormSelectMultiple,
    SearchDatePicker,
  },
  props: {
    variables: {
      type: Object,
      required: true,
    },
    showCategorySelect: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      date: {
        from: null,
        to: null,
      },
      localVariables: {
        search: undefined,
        categoryId: undefined,
        categories: undefined,
        from: undefined,
        to: undefined,
        page: undefined,
        order: 'PUBLISH_AT' as unknown as GetUniversalHeadlines['variables']['orderBy'],
        type: 'article',
      },
      searchFormData: {
        categories,
        order: [
          { value: 'RELEVANCE', name: this.$t('search.match') },
          { value: 'PUBLISH_AT', name: this.$t('search.latest') },
        ],
        type: [
          { value: 'article', name: this.$t('search.type.article') },
          { value: 'video', name: this.$t('search.type.video') },
          { value: 'audio', name: this.$t('search.type.audio') },
        ],
      },
      searchDatePickerSettings: {
        from: {},
        to: {},
      },
      isMobileView: false,
      faArrowRotateLeft,
      faMagnifyingGlass,
    };
  },
  watch: {
    date(value, oldValue) {
      let shouldFetch = false;

      if (value.from && value.from !== oldValue.from) {
        this.localVariables.from = value.from;
        shouldFetch = true;
      }

      if (value.to && value.to !== oldValue.to) {
        this.localVariables.to = value.to;
        shouldFetch = true;
      }

      if (shouldFetch) {
        this.$emit('submit', this.localVariables);
      }
    },
  },
  created() {
    this.localVariables = normalizeSearchObj(this.$props.variables);

    // Reset categories as array
    this.localVariables.categories = this.$props.variables?.categories;
  },
  methods: {
    handleReset() {
      this.localVariables.from = this.localVariables.to = this.localVariables.search = undefined;
      this.handleSetDate([]);
      this.handleSetCategory([]);
    },
    handleSetDate(value) {
      // Handle reset event
      if (!value.length) {
        this.localVariables.from = undefined;
        this.localVariables.to = undefined;

        this.submit();
        return;
      }

      this.localVariables.from = value[0].toISOString();
      this.localVariables.to = new Date(value[1].setHours(23, 59, 59, 999)).toISOString();

      this.submit();
    },
    handleSetCategory(values) {
      this.localVariables.categories = values;

      this.submit();
    },
    submit() {
      this.$emit('submit', this.localVariables);
    },
  },
  mounted() {
    this.isMobileView = isMobileView();
  },
});
